import React, { useMemo, useRef } from 'react';
import { UseAuth } from '@/contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import {
  transformAllUserWorkspaces,
  TransformedUserWorkspace,
} from '@/utils/data/transformWorkSpaceItems';
import { getWorkspace } from '@/apis/core/workspace/get/getWorkspace';
import {
  LoadingSpinner,
  Headers,
  AvatarGroup,
} from '@tw/tw-component-library';
import { Button, Modal } from '@/ui';
import {
  PageContainer,
  ContentSectionContainer,
  ContentSection,
  PlaceholderText,
} from '../workspacePage/WorkSpacePage.styles';
import { WorkspaceHeaderLayout } from '@/components/layout/WorkspaceHeaderLayout';
import { getAskSessionsByWorkspace } from '@/apis/ask/askHistory/get/getAskSessionsByWorkspace';
import { deleteAskSession } from '@/apis/ask/askHistory/delete/deleteAskSession';
import { AskSessionSession } from '@/apis/ask/askHistory/types';
import {
  AskSessionList,
  AskSessionHeader,
  AskSessionTitle,
  AskSessionDate,
  AskSessionMetaItem,
  AskSessionMetaLabel,
  AskSessionMetaValue,
  EmptyState,
  EmptyStateText,
  CreatorContainer,
  ContentSectionWithPadding,
  HeaderRow,
  ResultsCount,
  ThemeToggleContainer,
  ThemeToggle,
  ThemeToggleButton,
  AskHubContentBody,
  EditableTitleContainer,
  EditableTitleInput,
  SaveButton,
  CancelButton,
  EditButton,
  TitleWrapper,
  DeleteButton,
  DeleteModalContent,
  DeleteModalTitle,
  DeleteModalDetails,
  DeleteModalDetailRow,
  DeleteModalDetailLabel,
  DeleteModalDetailValue,
  DeleteModalActions,
  ThemedAskSessionItem,
  ThemedAskSessionMeta,
  AskHubThemeMode,
} from './AskHub.styles';
import { AskHubFilterPills } from './AskHubFilterPills';
import { Spacer } from '@tw/tw-component-library';
import {
  createWorkspaceUserLookup,
  DEFAULT_AVATAR_FALLBACK,
  formatDate,
} from './AskHub.utils';
import { useWorkspaceRelayHubHandlers } from '@/hooks/signalR/useWorkspaceRelayHubHandlers';
import { patchAskSession } from '@/apis/ask/askHistory/patch/patchAskSession';
import { useToast } from '@/components/common/Toast/ToastContainer';

export const AskHub: React.FC = () => {
  const THEME_STORAGE_KEY = 'litium-theme-mode';
  const { id } = useParams<{ id: string }>();
  const { getAccessToken, currentUser } = UseAuth();
  const [workspaceData, setWorkspaceData] =
    useState<TransformedUserWorkspace | null>(null);
  const [askSessions, setAskSessions] = useState<AskSessionSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  // Filter state
  const [filterCreator, setFilterCreator] = useState<string>('all');
  const [filterPersona, setFilterPersona] = useState<string>('all');
  const [themeMode, setThemeMode] = useState<AskHubThemeMode>('default');

  // Edit title state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editedTitle, setEditedTitle] = useState<string>('');
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation modal state
  const [sessionToDelete, setSessionToDelete] =
    useState<AskSessionSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Always subscribe to workspace-level SignalR events while on the Ask Hub page
  // so that file/workflow/collection/Ask session updates for this workspace are handled.
  useWorkspaceRelayHubHandlers({
    workspaceId: id,
    currentUserId: currentUser?.userId,
  });

  const getWorkspaceData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getWorkspace({
        getAccessToken: getAccessToken,
        workspaceId: id || '',
      });

      const transformed = await transformAllUserWorkspaces(
        [data],
        getAccessToken,
      );
      setWorkspaceData(transformed[0]);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to load workspace data');
      setError(error);
      console.error('Failed to load workspace data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [id, getAccessToken]);

  const fetchAskSessions = useCallback(async () => {
    if (!id) return;

    try {
      const sessions = await getAskSessionsByWorkspace({
        getAccessToken,
        workspaceId: id,
      });
      setAskSessions(sessions);
    } catch (err) {
      console.error('Failed to load ask sessions:', err);
    }
  }, [id, getAccessToken]);

  useEffect(() => {
    getWorkspaceData();
    fetchAskSessions();
  }, [getWorkspaceData, fetchAskSessions]);

  useEffect(() => {
    const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (storedTheme === 'compact' || storedTheme === 'default') {
      setThemeMode(storedTheme);
    }

    const handleThemeChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ mode?: AskHubThemeMode }>;
      if (customEvent.detail?.mode) {
        setThemeMode(customEvent.detail.mode);
      }
    };

    window.addEventListener(
      'litium-theme-mode-change',
      handleThemeChange as EventListener,
    );
    return () => {
      window.removeEventListener(
        'litium-theme-mode-change',
        handleThemeChange as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    if (themeMode === 'compact' && workspaceData?.id) {
      navigate(`/workspace/${workspaceData.id}/ask-session`, { replace: true });
    }
  }, [themeMode, workspaceData?.id, navigate]);

  const handleNewSession = useCallback(() => {
    if (workspaceData?.id) {
      navigate(`/workspace/${workspaceData.id}/ask-session`);
    }
  }, [workspaceData?.id, navigate]);

  const handleSessionClick = useCallback(
    (sessionId: string, e?: React.MouseEvent) => {
      // Don't navigate if clicking on edit or delete controls
      if (
        e &&
        (e.target as HTMLElement).closest(
          '.edit-title-controls, .delete-session-control',
        )
      ) {
        return;
      }
      if (workspaceData?.id && editingSessionId !== sessionId) {
        navigate(`/workspace/${workspaceData.id}/ask-session/${sessionId}`);
      }
    },
    [workspaceData?.id, navigate, editingSessionId],
  );

  const handleDeleteClick = useCallback(
    (session: AskSessionSession, e: React.MouseEvent) => {
      e.stopPropagation();
      setSessionToDelete(session);
    },
    [],
  );

  const handleCloseDeleteModal = useCallback(() => {
    if (!isDeleting) {
      setSessionToDelete(null);
    }
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!sessionToDelete) return;
    try {
      setIsDeleting(true);
      await deleteAskSession({
        getAccessToken,
        askSessionId: sessionToDelete.id,
      });
      setAskSessions((prev) => prev.filter((s) => s.id !== sessionToDelete.id));
      setSessionToDelete(null);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to delete session');
      console.error('Failed to delete ask session:', error);
      showError(`Failed to delete session: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  }, [sessionToDelete, getAccessToken, showError]);

  const handleStartEdit = useCallback(
    (session: AskSessionSession, e: React.MouseEvent) => {
      e.stopPropagation();
      setEditingSessionId(session.id);
      setEditedTitle(session.askTitle || '');
      // Focus input after state update
      setTimeout(() => {
        titleInputRef.current?.focus();
        titleInputRef.current?.select();
      }, 0);
    },
    [],
  );

  const handleCancelEdit = useCallback(
    (e?: React.MouseEvent | React.KeyboardEvent<HTMLInputElement>) => {
      if (e) {
        e.stopPropagation();
      }
      setEditingSessionId(null);
      setEditedTitle('');
    },
    [],
  );

  const handleSaveTitle = useCallback(
    async (
      sessionId: string,
      e?: React.MouseEvent | React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (e) {
        e.stopPropagation();
        if ('key' in e && e.key !== 'Enter') {
          return;
        }
      }

      const trimmedTitle = editedTitle.trim();
      if (!trimmedTitle) {
        showError('Title cannot be empty');
        return;
      }

      const session = askSessions.find((s) => s.id === sessionId);
      if (!session || session.askTitle === trimmedTitle) {
        handleCancelEdit();
        return;
      }

      try {
        setIsSavingTitle(true);
        const updatedSession = await patchAskSession({
          getAccessToken,
          askSessionId: sessionId,
          data: {
            title: trimmedTitle,
          },
        });

        // Update local state
        setAskSessions((prev) =>
          prev.map((s) =>
            s.id === sessionId
              ? { ...s, askTitle: updatedSession.askTitle }
              : s,
          ),
        );

        setEditingSessionId(null);
        setEditedTitle('');
        showSuccess('Title updated successfully');
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error('Failed to update title');
        console.error('Failed to update session title:', error);
        showError(`Failed to update title: ${error.message}`);
      } finally {
        setIsSavingTitle(false);
      }
    },
    [
      editedTitle,
      askSessions,
      getAccessToken,
      showSuccess,
      showError,
      handleCancelEdit,
    ],
  );

  // Single lookup over workspaceData so we don't call getters per user/session.
  const workspaceUserLookup = useMemo(
    () =>
      createWorkspaceUserLookup(workspaceData, {
        fallback: DEFAULT_AVATAR_FALLBACK,
      }),
    [workspaceData],
  );

  // Get unique creators and personas for filter options
  const { uniquePersonas, creatorOptions } = useMemo(() => {
    const creators = new Set<string>();
    const personas = new Set<string>();

    askSessions.forEach((session) => {
      creators.add(session.askCreator);
      if (session.askPersonaName) {
        personas.add(session.askPersonaName);
      }
    });

    const uniqueCreatorsArray = Array.from(creators);
    const creatorOptionsArray = uniqueCreatorsArray.map((creatorId) => ({
      id: creatorId,
      name: workspaceUserLookup.getDisplayName(creatorId),
    }));

    return {
      uniquePersonas: Array.from(personas).sort(),
      creatorOptions: creatorOptionsArray,
    };
  }, [askSessions, workspaceUserLookup]);

  // Filter sessions (sorted by newest first)
  const filteredSessions = useMemo(() => {
    let filtered = [...askSessions];

    // Apply creator filter
    if (filterCreator !== 'all') {
      filtered = filtered.filter(
        (session) => session.askCreator === filterCreator,
      );
    }

    // Apply persona filter
    if (filterPersona !== 'all') {
      filtered = filtered.filter(
        (session) => session.askPersonaName === filterPersona,
      );
    }

    // Sort by newest first
    filtered.sort((a, b) => {
      return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
    });

    return filtered;
  }, [askSessions, filterCreator, filterPersona]);

  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  if (error) {
    return (
      <PageContainer>
        <ContentSectionContainer>
          <ContentSection>
            <PlaceholderText>
              Error loading workspace: {error.message}
            </PlaceholderText>
          </ContentSection>
        </ContentSectionContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {!isLoading && !error && workspaceData && (
        <>
          <WorkspaceHeaderLayout
            workspaceData={workspaceData}
            activePage="ask-hub"
          />
          <ContentSectionContainer>
            <ContentSectionWithPadding>
              <ThemeToggleContainer>
                <ThemeToggle>
                  <ThemeToggleButton
                    type="button"
                    $active={themeMode === 'default'}
                    onClick={() => setThemeMode('default')}
                  >
                    Current
                  </ThemeToggleButton>
                  <ThemeToggleButton
                    type="button"
                    $active={themeMode === 'compact'}
                    onClick={() => setThemeMode('compact')}
                  >
                    Compact
                  </ThemeToggleButton>
                </ThemeToggle>
              </ThemeToggleContainer>

              <AskHubContentBody $themeMode={themeMode}>
                <HeaderRow>
                  <Headers headerType="h2">
                    Ask Sessions{' '}
                    {askSessions.length > 0 && `(${askSessions.length})`}
                  </Headers>
                  <Button
                    onClick={handleNewSession}
                    variant="primary"
                    data-tour="askhub-new-session-button"
                  >
                    + New Ask Session
                  </Button>
                </HeaderRow>

                {/* Filter Controls */}
                {askSessions.length > 0 && (
                  <>
                    <AskHubFilterPills
                      currentCreatorFilter={filterCreator}
                      onCreatorFilterChange={setFilterCreator}
                      currentPersonaFilter={filterPersona}
                      onPersonaFilterChange={setFilterPersona}
                      creators={creatorOptions}
                      personas={uniquePersonas}
                    />
                    <ResultsCount>
                      Showing {filteredSessions.length} of {askSessions.length}{' '}
                      session{askSessions.length !== 1 ? 's' : ''}
                    </ResultsCount>
                    <Spacer />
                  </>
                )}

                {askSessions.length === 0 ? (
                  <EmptyState>
                    <EmptyStateText>No ask sessions yet.</EmptyStateText>
                    <EmptyStateText>
                      Create a new session to get started!
                    </EmptyStateText>
                  </EmptyState>
                ) : (
                  <AskSessionList>
                    {filteredSessions.map((session) => {
                      const creatorAvatarData =
                        workspaceUserLookup.getAvatarData(session.askCreator);
                      // Fallback is set on lookup, so image is always displayable
                      const avatarUrl =
                        creatorAvatarData.image ?? DEFAULT_AVATAR_FALLBACK;

                      const isEditing = editingSessionId === session.id;

                      return (
                        <ThemedAskSessionItem
                          key={session.id}
                          $themeMode={themeMode}
                          onClick={(e) => handleSessionClick(session.id, e)}
                        >
                          <AskSessionHeader>
                            {isEditing ? (
                              <EditableTitleContainer className="edit-title-controls">
                                <EditableTitleInput
                                  ref={titleInputRef}
                                  value={editedTitle}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setEditedTitle(e.target.value);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      handleSaveTitle(session.id, e);
                                    } else if (e.key === 'Escape') {
                                      handleCancelEdit(e);
                                    }
                                    e.stopPropagation();
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  disabled={isSavingTitle}
                                />
                                <SaveButton
                                  onClick={(e) =>
                                    handleSaveTitle(session.id, e)
                                  }
                                  disabled={
                                    isSavingTitle || !editedTitle.trim()
                                  }
                                  title="Save (Enter)"
                                >
                                  ✓
                                </SaveButton>
                                <CancelButton
                                  onClick={(e) => handleCancelEdit(e)}
                                  disabled={isSavingTitle}
                                  title="Cancel (Esc)"
                                >
                                  ×
                                </CancelButton>
                              </EditableTitleContainer>
                            ) : (
                              <TitleWrapper>
                                <AskSessionTitle>
                                  {session.askTitle || 'Untitled Session'}
                                </AskSessionTitle>
                                <EditButton
                                  onClick={(e) => handleStartEdit(session, e)}
                                  className="edit-title-controls"
                                  title="Edit title"
                                >
                                  ✏️
                                </EditButton>
                                <DeleteButton
                                  onClick={(e) => handleDeleteClick(session, e)}
                                  className="delete-session-control"
                                  title="Delete session"
                                >
                                  🗑️
                                </DeleteButton>
                              </TitleWrapper>
                            )}
                            <AskSessionDate>
                              {formatDate(session.createdOn)}
                            </AskSessionDate>
                          </AskSessionHeader>
                          <ThemedAskSessionMeta $themeMode={themeMode}>
                            <AskSessionMetaItem>
                              <AskSessionMetaLabel>Persona</AskSessionMetaLabel>
                              <AskSessionMetaValue>
                                {session.askPersonaName || 'N/A'}
                              </AskSessionMetaValue>
                            </AskSessionMetaItem>
                            <AskSessionMetaItem>
                              <AskSessionMetaLabel>Creator</AskSessionMetaLabel>
                              <CreatorContainer>
                                <AvatarGroup
                                  size={32}
                                  avatars={[avatarUrl]}
                                  isOnline={[creatorAvatarData.isOnline]}
                                  userIds={[creatorAvatarData.userId]}
                                />
                              </CreatorContainer>
                            </AskSessionMetaItem>
                            <AskSessionMetaItem>
                              <AskSessionMetaLabel>
                                Thread Summary
                              </AskSessionMetaLabel>
                              <AskSessionMetaValue>
                                {session.askThreadSynopsis || 'N/A'}
                              </AskSessionMetaValue>
                            </AskSessionMetaItem>
                          </ThemedAskSessionMeta>
                        </ThemedAskSessionItem>
                      );
                    })}
                  </AskSessionList>
                )}
              </AskHubContentBody>
            </ContentSectionWithPadding>
          </ContentSectionContainer>

          {/* Delete confirmation modal */}
          <Modal
            isOpen={!!sessionToDelete}
            onClose={handleCloseDeleteModal}
            title="Delete Ask Session"
            footer={
              <DeleteModalActions>
                <Button
                  variant="outline"
                  onClick={handleCloseDeleteModal}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Confirm delete'}
                </Button>
              </DeleteModalActions>
            }
          >
            <DeleteModalContent>
              <DeleteModalTitle>
                Are you sure you wish to delete this ask session?
              </DeleteModalTitle>
              {sessionToDelete && (
                <>
                  <DeleteModalDetails>
                    <DeleteModalDetailRow>
                      <DeleteModalDetailLabel>Title</DeleteModalDetailLabel>
                      <DeleteModalDetailValue>
                        {sessionToDelete.askTitle || 'Untitled Session'}
                      </DeleteModalDetailValue>
                    </DeleteModalDetailRow>
                    <DeleteModalDetailRow>
                      <DeleteModalDetailLabel>Created</DeleteModalDetailLabel>
                      <DeleteModalDetailValue>
                        {formatDate(sessionToDelete.createdOn)}
                      </DeleteModalDetailValue>
                    </DeleteModalDetailRow>
                    <DeleteModalDetailRow>
                      <DeleteModalDetailLabel>Persona</DeleteModalDetailLabel>
                      <DeleteModalDetailValue>
                        {sessionToDelete.askPersonaName || 'N/A'}
                      </DeleteModalDetailValue>
                    </DeleteModalDetailRow>
                    <DeleteModalDetailRow>
                      <DeleteModalDetailLabel>Creator</DeleteModalDetailLabel>
                      <DeleteModalDetailValue>
                        {workspaceUserLookup.getDisplayName(
                          sessionToDelete.askCreator,
                        )}
                      </DeleteModalDetailValue>
                    </DeleteModalDetailRow>
                    <DeleteModalDetailRow>
                      <DeleteModalDetailLabel>
                        Thread Summary
                      </DeleteModalDetailLabel>
                      <DeleteModalDetailValue>
                        {sessionToDelete.askThreadSynopsis || 'N/A'}
                      </DeleteModalDetailValue>
                    </DeleteModalDetailRow>
                  </DeleteModalDetails>
                </>
              )}
            </DeleteModalContent>
          </Modal>
        </>
      )}
    </PageContainer>
  );
};
